import { z } from 'zod'

/**
 * People by exception (design 12, 19): notifications that reach staff, staffing (sites, holidays, rota)
 * and routing. Console API under /api/v1 (apps/api/src/http/console/people.ts).
 */

const HHMM = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use 24-hour HH:MM')
const Weekday = z.enum(['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'])

// ---------------------------------------------------------------------------
// Notifications
// ---------------------------------------------------------------------------
export const NotifyChannel = z.enum(['push', 'email', 'sms'])
export type NotifyChannel = z.infer<typeof NotifyChannel>
/** What a notification is about. Each kind can be sent on its own channels (prefs.kinds). */
export const NotifyKind = z.enum(['alarm', 'exception', 'handover', 'assigned', 'offered', 'escalation', 'call', 'learning', 'commitment', 'test'])
export type NotifyKind = z.infer<typeof NotifyKind>

/** The channels each kind uses unless a person chooses otherwise (a critical alarm also texts). */
export const NOTIFY_DEFAULT_KINDS: Record<NotifyKind, NotifyChannel[]> = {
  alarm: ['push', 'email'],
  exception: ['push'],
  handover: ['push', 'sms'],
  assigned: ['push', 'email'],
  offered: ['push'],
  escalation: ['push', 'email', 'sms'],
  call: ['push'],
  // The learning loop (services/staffNotifications.ts): changes to approve, autonomy earned or lost, roll-backs, audits.
  learning: ['push', 'email'],
  // Commitments in sent replies, to the company's monitoring addresses only (services/commitments.ts).
  commitment: ['email'],
  test: ['push', 'email', 'sms'],
}

export interface NotificationView {
  id: string
  kind: NotifyKind | string
  severity: 'info' | 'warning' | 'critical'
  title: string
  body: string
  url: string | null
  subject: { kind: string; id: string } | null
  created_at: string
  last_at: string
  repeat_count: number
  escalation_level: number
  read: boolean
  /** Whether "Got it" applies (an alarm or an item that is still open). */
  actionable: boolean
  deliveries: { channel: string; status: string; error: string | null }[]
}
export interface NotificationFeed { items: NotificationView[]; unread: number; next_cursor: string | null }

export const NotificationPrefsInput = z.object({
  channels: z.object({ push: z.boolean(), email: z.boolean(), sms: z.boolean() }).partial().default({}),
  /** Per kind, the channels it may use; a kind not listed uses `channels`. An empty list means in-app only. */
  kinds: z.partialRecord(NotifyKind, z.array(NotifyChannel)).default({}),
  quiet_start: HHMM.nullish(),
  quiet_end: HHMM.nullish(),
  timezone: z.string().max(64).nullish(),
  critical_overrides_quiet: z.boolean().default(true),
}).refine((p) => (p.quiet_start == null) === (p.quiet_end == null), { message: 'Set both quiet hours times, or neither', path: ['quiet_end'] })
export type NotificationPrefsInput = z.infer<typeof NotificationPrefsInput>
export interface NotificationPrefsView extends Omit<NotificationPrefsInput, 'quiet_start' | 'quiet_end' | 'timezone'> {
  quiet_start: string | null; quiet_end: string | null; timezone: string | null
  /** Where each channel would go, and why it cannot (no phone number, push not set up on this cell...). */
  reach: { push: { devices: number; available: boolean; why: string | null }; email: { to: string; available: boolean; why: string | null }; sms: { to: string | null; available: boolean; why: string | null } }
}

/** A browser's PushSubscription.toJSON(). */
export const PushSubscriptionInput = z.object({
  endpoint: z.url().max(2000),
  keys: z.object({ p256dh: z.string().min(40).max(200), auth: z.string().min(8).max(100) }),
  user_agent: z.string().max(300).optional(),
})

/** A company's alerting policy (admin). */
export const AlertPolicy = z.object({
  /** Minutes a critical alarm or a priority-1 item may go unacknowledged before the next person is told. */
  ack_minutes: z.number().int().min(2).max(240).default(10),
  /** The chain after the people on duty: supervisors, then admins. */
  chain: z.array(z.enum(['on_duty', 'supervisor', 'admin'])).min(1).default(['on_duty', 'supervisor', 'admin']),
  /** After the end of the chain, reminders to the last level (capped). */
  max_reminders: z.number().int().min(0).max(10).default(2),
  /**
   * After the chain and its reminders, a critical alarm nobody acknowledged is repeated to the last level this
   * often, with no limit, until someone acknowledges it or it clears (critical alerts are never dropped).
   * 0 turns the repeat off.
   */
  repeat_minutes: z.number().int().min(0).max(24 * 60).default(60),
  /** Copy this company's outcome and filtering alarms to takeit4me's operators too. */
  operator_copy: z.boolean().default(true),
  /** The same thing is not sent to the same person again within this window (in-app it is counted instead). */
  cooldown_minutes: z.number().int().min(1).max(24 * 60).default(30),
  /** At most this many external notifications per person per hour, except critical ones. */
  hourly_cap: z.number().int().min(1).max(200).default(12),
  /** Which alarms are delivered outside the console, by severity. */
  min_severity: z.enum(['info', 'warning', 'critical']).default('warning'),
  /** An optional Slack-compatible webhook (https) that receives every delivered alarm. */
  webhook_url: z.url().startsWith('https://').nullish(),
  /** A warning nobody acknowledged is sent once more after this many hours, one level up the chain too (0: never). Default: one page and one reminder. */
  warning_reminder_hours: z.number().int().min(0).max(168).default(24),
  /**
   * Who is emailed when takeit4me itself is down or degraded for this company (the cell, or a background step
   * customers depend on). Kept in the control plane too, so the watchdog can reach them while the cell is down.
   */
  outage_emails: z.array(z.email()).max(5).default([]),
})
export type AlertPolicy = z.infer<typeof AlertPolicy>

// ---------------------------------------------------------------------------
// Staffing
// ---------------------------------------------------------------------------
export const SiteInput = z.object({
  name: z.string().min(1).max(120),
  timezone: z.string().min(3).max(64),
  hours: z.partialRecord(Weekday, z.array(z.tuple([HHMM, HHMM]))).default({}),
  address: z.string().max(300).nullish(),
})
export interface SiteView { id: string; name: string; timezone: string; hours: Partial<Record<z.infer<typeof Weekday>, [string, string][]>>; address: string | null; open_now: boolean }

export const ClosureInput = z.object({
  site_id: z.uuid().nullish(),
  day: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  name: z.string().min(1).max(120),
  /** Shortened hours that day; omit or null for closed all day. */
  hours: z.array(z.tuple([HHMM, HHMM])).nullish(),
})
export interface ClosureView { id: string; site_id: string | null; site: string | null; day: string; name: string; hours: [string, string][] | null }

export const RotaShiftInput = z.object({
  staff_id: z.uuid(),
  site_id: z.uuid().nullish(),
  weekday: Weekday,
  start_time: HHMM,
  end_time: HHMM,
}).refine((s) => s.start_time !== s.end_time, { message: 'A shift needs a start and an end', path: ['end_time'] })
export const ShiftOverrideInput = z.object({
  staff_id: z.uuid(),
  kind: z.enum(['off', 'on']),
  starts_at: z.iso.datetime({ offset: true }),
  ends_at: z.iso.datetime({ offset: true }),
  note: z.string().max(300).nullish(),
})
export const DutyModeInput = z.object({ duty_mode: z.enum(['manual', 'rota']) })

export interface RotaView {
  shifts: { id: string; staff_id: string; site_id: string | null; weekday: string; start_time: string; end_time: string }[]
  overrides: { id: string; staff_id: string; kind: 'off' | 'on'; starts_at: string; ends_at: string; note: string | null }[]
  staff: { id: string; name: string; role: string; duty_mode: 'manual' | 'rota'; on_duty: boolean; on_duty_changed_at: string | null; sites: string[]; skills: string[]; languages: string[]; open_items: number; scheduled_now: boolean | null }[]
}

/** Assign or offer an exception to a colleague (Exceptions desk), or let routing choose. */
export const ExceptionRouteInput = z.discriminatedUnion('action', [
  z.object({ action: z.literal('assign'), staff_id: z.uuid(), note: z.string().max(300).optional() }),
  z.object({ action: z.literal('unassign') }),
  z.object({ action: z.literal('auto') }),
])
